import api from './api'
import * as fb from './facebook'
import { getUTMParams } from './UTMManager'
import Hashes from 'jshashes'
import * as apple from './apple'
import { getTimestamp, getDate } from './utils'
import { BE_HEALTHIER } from './goals'

const CLIENT_ID = 'bcbb5d070e1c55e9ab963d95bd5566c4'
const CLIENT_SECRET =
  '253666026bc851b7de9eb16b901e592c0f80356687ea4985334be0c0ac46e49f'

export const sha256 = message => new Hashes.SHA256().hex(message)

export const createAccount = ({ weightUnit, heightUnit, ...userData }) => {
  const timestamp = parseInt(getTimestamp() / 1000)
  const useskj = ['AU', 'NZ', 'ZA'].includes(userData.country)

  let usesmetric = false
  let usesstone = false

  if (heightUnit === 'cm' && weightUnit === 'kg') {
    usesmetric = true
    usesstone = false
  } else if (heightUnit === 'ft' && weightUnit === 'lb') {
    usesmetric = false
    usesstone = false
  } else if (heightUnit === 'cm' && weightUnit === 'st') {
    usesmetric = true
    usesstone = true
  }

  const postData = {
    email: userData.email,
    pass: userData.password ? userData.password : '',
    gender: userData.gender,
    loseweight: parseInt(userData.goal, 10),
    weight: parseInt(userData.weight, 10),
    height: parseInt(userData.height, 10),
    targetweight:
      userData.goal === BE_HEALTHIER
        ? parseInt(userData.weight, 10)
        : parseInt(userData.goalWeight, 10),
    useskj,
    usesmetric,
    usesstone,
    hash: sha256(`$${userData.email}$${timestamp}$${CLIENT_SECRET}`),
    firstname: userData.firstName,
    lastname: userData.lastName ? userData.lastName : '',
    timestamp: timestamp,
    country: userData.country,
    api_key: CLIENT_ID,
    device: 'web',
    activity: 1.375, // Same default as apps
    lossperweek: 0.5, // Same default as apps
    birthdate: getDate().getUTCFullYear() - userData.age + '-01-01',
  }

  if (userData.oauth_service && userData.oauth_token) {
    postData.oauth_service = userData.oauth_service
    postData.oauth_token = userData.oauth_token
  }

  if (getUTMParams()) {
    postData.web_attribution = getUTMParams()
  }

  return api('POST', 'v2/accounts/create', postData).then(
    ({ response: { token, userid } }) => ({
      userid,
      token,
    })
  )
}

export const getUserProfile = () => {
  return Promise.all([
    api('GET', 'userprofile/v1/me', undefined, { cache: 'no-store' }),
    api('GET', 'v2/store/subscription'),
  ]).then(([profile, { response }]) => {
    return { ...profile, ...response }
  })
}

export const updateUserProfile = data => {
  return api('PUT', 'userprofile/v2/me', data)
}

export const verifyAccountEmail = code => {
  return api(
    'POST',
    `v2/accounts/verify_account/${code}/`
  ).then(({ response: { token, userid } }) => ({ token, userid }))
}

export const deleteAccount = userid => {
  return api('DELETE', 'v2/accounts/account_delete', {
    userid,
  }).then(res => {
    return res.response
  })
}

export const cancelSubscription = () => {
  return api('DELETE', 'v2/store/subscription')
}

export const checkEmail = email => {
  const timestamp = parseInt(new Date().getTime() / 1000)

  return api('POST', 'v2/accounts/check_email', {
    email: email,
    timestamp: timestamp,
    hash: sha256(`$${email}$${timestamp}$${CLIENT_SECRET}`),
    api_key: CLIENT_ID,
  })
}

export const requestPasswordReset = email => {
  return api('POST', 'v2/accounts/request_password_reset', {
    email,
  }).then(res => {
    return res.response
  })
}

export const confirmPasswordReset = (token, newPassword) => {
  return api('POST', 'v2/accounts/confirm_password_reset', {
    code: token,
    new_password: newPassword,
  }).then(({ response: { token, userid } }) => ({ token, userid }))
}

export const changePassword = ({
  currentPassword: oldpassword,
  newPassword: newpassword,
}) => {
  return api('POST', 'v2/accounts/changepass', {
    oldpassword,
    newpassword,
  }).then(res => {
    return res.response
  })
}

export const getAccountInfoByUserId = id => {
  return api('GET', `v2/accounts/account_info/${id}`).then(res => {
    return res.response
  })
}

export const loginWithEmail = (email, password) => {
  const timestamp = getTimestamp()
  return api('POST', 'v2/accounts/authenticate', {
    email: email,
    pass: password,
    timestamp: timestamp,
    api_key: CLIENT_ID,
    hash: sha256(`$${email}$${timestamp}$${CLIENT_SECRET}`),
  }).then(({ response: { token, userid } }) => ({ token, userid }))
}

export const loginWithFacebook = () => {
  return fb.login().then(({ token }) => {
    return fb.getUserDetails().then(({ email }) => {
      return authenticateWithService('facebook', { token, email })
    })
  })
}

export const loginWithApple = () => {
  return apple.login().then(({ apple_uid }) => {
    return authenticateWithService('apple', { apple_uid })
  })
}

export const authenticateWithService = (service, data = {}) => {
  const timestamp = getTimestamp()
  const params = {
    oauth_service: service,
    platform: 'web',
    timestamp: timestamp,
    api_key: CLIENT_ID,
  }

  if (service === 'facebook') {
    params.oauth_token = data.token
    params.hash = sha256(`$${data.token}$${timestamp}$${CLIENT_SECRET}`)
    params.email = data.email
  }

  if (service === 'apple') {
    params.apple_userid = data.apple_uid
    params.hash = sha256(`$${data.apple_uid}$${timestamp}$${CLIENT_SECRET}`)
  }

  return api(
    'POST',
    'v2/accounts/authenticate',
    params
  ).then(({ response: { token, userid } }) => ({ token, userid }))
}

export const getFacebookUserDetails = () => {
  return fb.login().then(({ token }) => {
    return fb.getUserDetails().then(response => {
      response.token = token
      return response
    })
  })
}

export const getUserCountry = () => {
  let fallback = 'US'

  return api('GET', 'v2/country')
    .then(result =>
      result.response.country ? result.response.country : fallback
    )
    .catch(() => fallback)
}
